import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { ControllerApiPath } from '../services/constant';
import { SharedServiceService } from '../services/shared-service.service';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FitnessPlanService {
  private apiBaseUrl: string;

  constructor(
    private http: HttpClient,
    private sharedService: SharedServiceService
  ) {
    this.apiBaseUrl = sharedService.baseUrl();
  }

  getMemberDetails(id: any) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.members +
      '/GetMemberById?memberId=' +
      id;
    return this.http.get(url, { headers });
  }

  getVital(id: any) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.vital +
      '?memberId=' +
      id +
      '&date=0001-01-01T00:00:00';
    return this.http.get(url, { headers });
  }

  checkIsFitnessPlanOngoing(memberId: string, startDate: string, endDate?: string) {
    const headers = this.sharedService.createAuthorizationHeader();
    let url = this.apiBaseUrl + ControllerApiPath.fitnessPlan + '/checkifongoing?memberId=' + memberId + '&startDate=' + startDate;
    
    if (endDate) {
      url += '&endDate=' + endDate;
    }
    
    return this.http.get(url, { headers });
  }

  getFPAllMembers(page: any, pageSize: any) {
    const headers = this.sharedService.createAuthorizationHeader();
    let user = this.sharedService.getLoggedInUser();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.fitnessPlan +
      '/getallmembers?page=' +
      page +
      '&pageSize=' +
      pageSize +
      '&loggedUserId=' +
      user.id;
    return this.http.get(url, { headers });
  }

  getAllReports(memberId: any) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.fitnessPlan +
      '/getallreports?memberId=' +
      memberId;
    return this.http.get(url, { headers });
  }

  searchFPPerMember(page: any, pageSize: any, searchTerm: any) {
    const headers = this.sharedService.createAuthorizationHeader();
    let user = this.sharedService.getLoggedInUser();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.fitnessPlan +
      '/searchunassignedmemebrs?page=' +
      page +
      '&pageSize=' +
      pageSize +
      '&loggedUserId=' +
      user.id +
      '&searchTerm=' +
      searchTerm;
    return this.http.get(url, { headers });
  }

  reAssignFP(data: any) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url = this.apiBaseUrl + ControllerApiPath.fitnessPlan + '/reassign';
    return this.http.post(url, data, { headers });
  }

  uploadPlan(data: any) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url = this.apiBaseUrl + ControllerApiPath.fitnessPlan + '/assign';
    return this.http.post(url, data, { headers }).pipe(
      map((response) => response),
      catchError((error) => {
        console.log(error);
        return of(1);
      })
    );
  }

  createFitnessPlan(data: any) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl + ControllerApiPath.fitnessPlan + '/exercise-allotments';
    
    console.log('FitnessPlanService - Sending data to backend:');
    console.log('URL:', url);
    console.log('Data:', JSON.stringify(data, null, 2));
    
    return this.http.post(url, data, { headers }).pipe(
      map((response) => response),
      catchError((error) => {
        console.error('Error creating fitness plan:', error);
        throw error; // Re-throw to let component handle the error
      })
    );
  }

  getExercises() {
    const headers = this.sharedService.createAuthorizationHeader();
    const url = this.apiBaseUrl + ControllerApiPath.fitnessPlan + '/exercises';
    // console.log('Fetching exercises from URL:', url);
    // console.log('Headers:', headers);

    return this.http.get(url, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error('Error fetching exercises:', error);
        // Return empty exercises array if API fails
        return of({
          exercises: [],
        });
      })
    );
  }

  getCurrentUserId(): string {
    const user = this.sharedService.getLoggedInUser();
    return user ? user.id : '';
  }

  getExerciseAllotments(memberId: string) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.fitnessPlan +
      '/exercise-allotments/member/' +
      memberId;
    // console.log('Fetching exercise allotments from URL:', url);

    return this.http.get(url, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error('Error fetching exercise allotments:', error);
        return of({
          allotments: [],
        });
      })
    );
  }

  // === FITNESS PLAN TEMPLATE CRUD OPERATIONS ===

  // Create Template: POST /api/fitness-plan-templates
  createFitnessPlanTemplate(data: any) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url = this.apiBaseUrl + ControllerApiPath.fitnessPlanTemplates;
    return this.http.post(url, data, { headers }).pipe(
      map((response) => response),
      catchError((error) => {
        console.error('Error creating fitness plan template:', error);
        throw error;
      })
    );
  }

  // Get All Templates: GET /api/fitness-plan-templates
  getAllFitnessPlanTemplates() {
    const headers = this.sharedService.createAuthorizationHeader();
    // console.log(headers);
    const url = this.apiBaseUrl + ControllerApiPath.fitnessPlanTemplates;
    return this.http.get(url, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error('Error fetching fitness plan templates:', error);
        return of({
          templates: [],
          totalCount: 0,
        });
      })
    );
  }

  // Get Template by ID: GET /api/fitness-plan-templates/{id}
  getFitnessPlanTemplateById(id: string | number) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl + ControllerApiPath.fitnessPlanTemplates + `/${id}`;
    return this.http.get(url, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error('Error fetching fitness plan template by ID:', error);
        throw error;
      })
    );
  }

  // Update Template: PUT /api/fitness-plan-templates/{id}
  updateFitnessPlanTemplate(id: string | number, data: any) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl + ControllerApiPath.fitnessPlanTemplates + `/${id}`;
    return this.http.put(url, data, { headers }).pipe(
      map((response) => response),
      catchError((error) => {
        console.error('Error updating fitness plan template:', error);
        throw error;
      })
    );
  }

  // Delete Template: DELETE /api/fitness-plan-templates/{id}
  deleteFitnessPlanTemplate(id: string | number) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl + ControllerApiPath.fitnessPlanTemplates + `/${id}`;
    return this.http.delete(url, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error('Error deleting fitness plan template:', error);
        throw error;
      })
    );
  }

  // Search Templates: GET /api/fitness-plan-templates/search?searchTerm={term}
  searchFitnessPlanTemplates(searchTerm: string) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.fitnessPlanTemplates +
      `/search?searchTerm=${encodeURIComponent(searchTerm)}`;
    return this.http.get(url, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error('Error searching fitness plan templates:', error);
        return of({
          templates: [],
          totalCount: 0,
        });
      })
    );
  }

  // === END FITNESS PLAN TEMPLATE CRUD OPERATIONS ===

  deleteExerciseAllotment(allotmentIds: string[]) {
    let headers = this.sharedService.createAuthorizationHeader();

    // Add content-type header for JSON payload
    headers = headers.set('Content-Type', 'application/json');

    const url =
      this.apiBaseUrl + ControllerApiPath.fitnessPlan + '/exercise-allotments';

    // Create payload object with ids property to match API expected format
    const payload = {
      ids: allotmentIds,
    };

    // console.log('Deleting exercise allotments with payload:', payload);

    return this.http
      .delete(url, {
        headers,
        body: payload,
      })
      .pipe(
        map((response: any) => response),
        catchError((error) => {
          console.error('Error deleting exercise allotments:', error);
          throw error; // Re-throw to let component handle the error
        })
      );
  }

  // === EXERCISE INSTRUCTIONS MANAGEMENT ===

  // Get exercise instructions for a specific member
  getMemberExerciseInstructions(memberId: string) {
    const headers = this.sharedService.createAuthorizationHeader();

    const url =
      this.apiBaseUrl +
      ControllerApiPath.fitnessPlan +
      `/member-exercise-instructions/member/${memberId}`;
    // console.log('Fetching exercise instructions from URL:', url);

    return this.http.get(url, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error('Error fetching exercise instructions:', error);
        return of({
          exercises: [],
        });
      })
    );
  }

  // Get exercise instructions by ID
  getExerciseInstructionsById(id: string) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.fitnessPlan +
      `/member-exercise-instructions/${id}`;

    return this.http.get(url, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error('Error fetching exercise instructions by ID:', error);
        throw error;
      })
    );
  }

  // Get exercise instructions by exercise ID
  getExerciseInstructionsByExercise(exerciseId: string) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.fitnessPlan +
      `/member-exercise-instructions/exercise/${exerciseId}`;

    return this.http.get(url, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error(
          'Error fetching exercise instructions by exercise:',
          error
        );
        return of({
          instructions: [],
        });
      })
    );
  }

  // Get exercise instructions by member and exercise
  getExerciseInstructionsByMemberAndExercise(
    memberId: string,
    exerciseId: string
  ) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.fitnessPlan +
      `/member-exercise-instructions/member/${memberId}/exercise/${exerciseId}`;

    return this.http.get(url, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error(
          'Error fetching exercise instructions by member and exercise:',
          error
        );
        return of(null);
      })
    );
  }

  // Save/update exercise instructions for a member
  saveExerciseInstructions(instructionData: any) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.fitnessPlan +
      `/member-exercise-instructions`;

    return this.http.post(url, instructionData, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error('Error saving exercise instructions:', error);
        throw error;
      })
    );
  }

  // Delete exercise instructions by ID
  deleteExerciseInstructionsById(id: string) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.fitnessPlan +
      `/member-exercise-instructions/${id}`;

    return this.http.delete(url, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error('Error deleting exercise instructions:', error);
        throw error;
      })
    );
  }

  // Get detailed exercise information for all exercises allotted to a member
  getMemberExerciseDetails(memberId: string) {
    const headers = this.sharedService.createAuthorizationHeader();
    const url =
      this.apiBaseUrl +
      ControllerApiPath.fitnessPlan +
      `/member-exercise-details/member/${memberId}`;
    // console.log('Fetching member exercise details from URL:', url);

    return this.http.get(url, { headers }).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error('Error fetching member exercise details:', error);
        return of({
          exercises: [],
          totalCount: 0,
          member: null,
        });
      })
    );
  }

  // === END EXERCISE INSTRUCTIONS MANAGEMENT ===
}
